import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { ProductCard } from "../components/ProductCard";
import { ArrowRight } from "lucide-react";

export default function Home() {
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  const banner = banners[0];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[92vh] min-h-[640px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={banner?.image_url || "https://images.pexels.com/photos/2820574/pexels-photo-2820574.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800"}
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col justify-end pb-24">
          <div className="max-w-2xl stagger">
            <div className="overline text-white/80">{banner?.subtitle ? "Edition 001" : "New Arrivals"}</div>
            <h1 className="serif text-5xl md:text-7xl lg:text-8xl font-light leading-[1.02] mt-6">
              {banner?.title || "Winter Collection 2026"}
            </h1>
            <p className="mt-6 text-base md:text-lg font-light text-white/80 max-w-xl">
              {banner?.subtitle || "Enduring silhouettes. A study in restraint."}
            </p>
            <div className="mt-10 flex gap-4">
              <Link to={banner?.cta_link || "/shop"} className="btn-sharp primary" data-testid="hero-cta-primary">
                {banner?.cta_text || "Explore The Edit"}
              </Link>
              <Link to="/shop/women" className="btn-sharp ghost text-white" data-testid="hero-cta-ghost">
                Women's
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Tetris Grid */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline text-muted-foreground">Chapter I</div>
            <h2 className="serif text-4xl md:text-5xl mt-3">The Collections</h2>
          </div>
          <Link to="/shop" className="text-xs uppercase tracking-[0.22em] flex items-center gap-2 hover:text-muted-foreground">
            Browse all <ArrowRight size={14} strokeWidth={1.2} />
          </Link>
        </div>
        <div className="grid grid-cols-12 grid-rows-2 gap-3 md:gap-4 h-[760px]">
          {categories[0] && (
            <Link to={`/shop/${categories[0].slug}`} className="col-span-12 md:col-span-7 row-span-2 relative group overflow-hidden product-image-wrap" data-testid="cat-card-0">
              <img src={categories[0].image} alt={categories[0].name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8">
                <div className="overline text-white/80">Collection</div>
                <div className="serif text-5xl md:text-6xl text-white mt-2">{categories[0].name}</div>
              </div>
            </Link>
          )}
          {categories[1] && (
            <Link to={`/shop/${categories[1].slug}`} className="col-span-12 md:col-span-5 row-span-1 relative group overflow-hidden product-image-wrap" data-testid="cat-card-1">
              <img src={categories[1].image} alt={categories[1].name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <div className="overline text-white/80">Collection</div>
                <div className="serif text-4xl text-white mt-1">{categories[1].name}</div>
              </div>
            </Link>
          )}
          {categories[2] && (
            <Link to={`/shop/${categories[2].slug}`} className="col-span-12 md:col-span-5 row-span-1 relative group overflow-hidden product-image-wrap" data-testid="cat-card-2">
              <img src={categories[2].image} alt={categories[2].name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <div className="overline text-white/80">Collection</div>
                <div className="serif text-4xl text-white mt-1">{categories[2].name}</div>
              </div>
            </Link>
          )}
        </div>
      </section>

      {/* Featured Products */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 border-t border-white/10">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline text-muted-foreground">Chapter II</div>
            <h2 className="serif text-4xl md:text-5xl mt-3">Selected Pieces</h2>
          </div>
          <Link to="/shop" className="text-xs uppercase tracking-[0.22em] hover:text-muted-foreground">View all</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {featured.slice(0, 4).map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* Editorial statement */}
      <section className="mx-auto max-w-[1200px] px-6 md:px-12 py-32 text-center">
        <div className="overline text-muted-foreground">Velora</div>
        <p className="serif text-3xl md:text-5xl mt-6 leading-[1.2] font-light italic">
          "We make clothes for the quiet confidence of later — the fifth wear, the tenth, the garment worn into memory."
        </p>
        <div className="mt-8 text-xs uppercase tracking-[0.22em] text-muted-foreground">— The Atelier</div>
      </section>
    </div>
  );
}
